/**
 * Operation Executor
 *
 * Applies operations from AI agents to the TipTap editor.
 * Handles insert, replace, delete, and append operations.
 */

import { Editor } from "@tiptap/react";
import {
  OperationPatch,
  CoreOperation,
  InsertOperation,
  ReplaceOperation,
  DeleteOperation,
  AppendOperation,
  CommentOperation,
  EditOperation,
  MoveOperation,
  SetAttrsOperation,
  AcceptChangeOperation,
  RejectChangeOperation,
  AcceptAllChangesOperation,
  RejectAllChangesOperation,
  RemoveCommentOperation,
  ReplyCommentOperation,
  ExecutionResult,
  PatchExecutionResult,
  NodeType,
  NodeAttrs,
  ContentType,
  FormattedTextSegment,
  MarkType,
  CommentData,
  isInsertOperation,
  isReplaceOperation,
  isDeleteOperation,
  isAppendOperation,
  isCommentOperation,
  isEditOperation,
  isMoveOperation,
  isSetAttrsOperation,
  isAcceptChangeOperation,
  isRejectChangeOperation,
  isAcceptAllChangesOperation,
  isRejectAllChangesOperation,
  isRemoveCommentOperation,
  isReplyCommentOperation,
} from "./types";
import { findNodeById, generateNodeId } from "../extensions/UniqueIdExtension";
import { computeWordDiff, DiffOp } from "./wordDiff";

/**
 * Stylistic marks that should NOT be inherited when inserting new content.
 * These are typically user-applied emphasis marks that shouldn't automatically
 * apply to new text just because it's inserted near styled text.
 */
const STYLISTIC_MARK_TYPES = new Set([
  "bold",
  "italic",
  "underline",
  "strike",
  "superscript",
  "subscript",
  "code",
  "highlight",
]);

/**
 * Get inheritable formatting marks from a node (color, font, etc.).
 * Used when inserting text so new text matches the paragraph's styling.
 * Excludes metadata marks AND stylistic marks.
 */
function getInheritableMarks(node: {
  descendants: (f: (n: any) => boolean) => void;
}): any[] {
  const seen = new Set<string>();
  const marks: any[] = [];
  node.descendants(n => {
    if (n.isText && n.marks && n.marks.length > 0) {
      for (const m of n.marks) {
        if (!NON_INHERITABLE_MARKS.has(m.type.name) && !seen.has(m.type.name)) {
          seen.add(m.type.name);
          marks.push(m);
        }
      }
      return false; // Stop after first text node with marks
    }
    return true;
  });
  return marks;
}

/**
 * Execute all operations in a patch
 *
 * @param editor - The TipTap editor instance
 * @param patch - The patch containing operations to execute
 * @returns Result of the patch execution
 */
export function executeOperations(
  editor: Editor,
  patch: OperationPatch
): PatchExecutionResult {
  const results: ExecutionResult[] = [];
  const comments: CommentData[] = [];
  let failedCount = 0;

  // Track insert redirects: when consecutive inserts target the same node
  // with position "after", redirect subsequent ones to the just-inserted node
  // so they appear in the intended order (not reversed).
  // Maps original targetNodeId -> most recently inserted nodeId for "after" inserts
  const insertAfterRedirects = new Map<string, string>();

  for (let i = 0; i < patch.operations.length; i++) {
    const op = patch.operations[i];

    // Apply insert-after redirect if applicable
    if (isInsertOperation(op) && op.position === "after") {
      const originalTarget = op.target.nodeId;
      const redirect = insertAfterRedirects.get(originalTarget);
      if (redirect) {
        // Save the original target for redirect map keying
        (op as any)._originalTarget = originalTarget;
        (op as any).target = { ...op.target, nodeId: redirect };
      }
    }

    const result = executeOperation(editor, op, i);
    results.push(result);

    if (!result.success) {
      failedCount++;
    } else if (isInsertOperation(op) && op.position === "after" && result.nodeId) {
      // After a successful "after" insert, redirect future inserts that
      // targeted the same original node to this newly inserted node
      const originalTarget = (op as any)._originalTarget || op.target.nodeId;
      insertAfterRedirects.set(originalTarget, result.nodeId);
      // Also redirect inserts targeting the new node itself (for chains)
      insertAfterRedirects.set(op.target.nodeId, result.nodeId);
    }

    // Collect comment data if this was a comment operation
    if (result.commentData) {
      comments.push(result.commentData);
    }
  }

  // === DIAGNOSTIC: dump final document node order ===
  const finalDoc = editor.state.doc;
  const nodeOrder: string[] = [];
  finalDoc.forEach((node, _offset, index) => {
    const id = node.attrs?.id || `(no-id-${index})`;
    const text = node.textContent?.substring(0, 50) || "";
    nodeOrder.push(`${id}: "${text}"`);
  });
  // === END DIAGNOSTIC ===

  return {
    success: failedCount === 0,
    results,
    failedCount,
    comments,
  };
}

/**
 * Execute a single operation
 */
function executeOperation(
  editor: Editor,
  op: CoreOperation,
  index: number
): ExecutionResult {
  try {
    if (isInsertOperation(op)) {
      return executeInsert(editor, op, index);
    } else if (isReplaceOperation(op)) {
      return executeReplace(editor, op, index);
    } else if (isDeleteOperation(op)) {
      return executeDelete(editor, op, index);
    } else if (isAppendOperation(op)) {
      return executeAppend(editor, op, index);
    } else if (isCommentOperation(op)) {
      return executeComment(editor, op, index);
    } else if (isEditOperation(op)) {
      return executeEdit(editor, op, index);
    } else if (isMoveOperation(op)) {
      return executeMove(editor, op, index);
    } else if (isSetAttrsOperation(op)) {
      return executeSetAttrs(editor, op, index);
    } else if (isAcceptChangeOperation(op)) {
      return executeAcceptChange(editor, op, index);
    } else if (isRejectChangeOperation(op)) {
      return executeRejectChange(editor, op, index);
    } else if (isAcceptAllChangesOperation(op)) {
      return executeAcceptAllChanges(editor, op, index);
    } else if (isRejectAllChangesOperation(op)) {
      return executeRejectAllChanges(editor, op, index);
    } else if (isRemoveCommentOperation(op)) {
      return executeRemoveComment(editor, op, index);
    } else if (isReplyCommentOperation(op)) {
      return executeReplyComment(editor, op, index);
    } else {
      return {
        success: false,
        operationIndex: index,
        error: `Unknown operation type`,
      };
    }
  } catch (error) {
    return {
      success: false,
      operationIndex: index,
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

/**
 * Execute an insert operation
 */
function executeInsert(
  editor: Editor,
  op: InsertOperation,
  index: number
): ExecutionResult {
  const doc = editor.state.doc;
  const targetResult = findNodeById(doc, op.target.nodeId);

  if (!targetResult) {
    return {
      success: false,
      operationIndex: index,
      error: `Node not found: ${op.target.nodeId}`,
    };
  }

  const { node: targetNode, pos: targetPos } = targetResult;
  // Use pre-generated ID from the operation if provided (for cache sync),
  // otherwise generate a new one
  const newNodeId = op.newNodeId || generateNodeId();

  // Inherit formatting from the target node so the new node matches
  // the surrounding document's color, font, indentation, etc.
  const inheritedMarks = extractInlineMarks(targetNode);
  const inheritedAttrs = extractFormattingAttrs(targetNode);

  // Calculate insert position
  let insertPos =
    op.position === "before" ? targetPos : targetPos + targetNode.nodeSize;

  // === DIAGNOSTIC LOGGING ===

  // ── Nested-list redirect ──
  // When inserting a listItem "after" a listItem that CONTAINS a nested
  // bulletList/orderedList, the user almost always wants to append to the
  // nested list — not add a sibling at the parent level.  Redirect the
  // insert position to the end of the nested list's content.
  if (
    op.nodeType === "listItem" &&
    op.position === "after" &&
    targetNode.type.name === "listItem"
  ) {
    let nestedListEnd: number | null = null;
    targetNode.forEach((child, offset) => {
      if (
        child.type.name === "bulletList" ||
        child.type.name === "orderedList"
      ) {
        // Position just before the closing tag of the nested list
        const nestedListPos = targetPos + 1 + offset; // +1 for listItem open
        nestedListEnd = nestedListPos + child.nodeSize - 1;
      }
    });
    if (nestedListEnd !== null) {
      insertPos = nestedListEnd;
    }
  }

  // Merge inherited attrs with explicitly provided attrs (explicit wins)
  const mergedAttrs = { ...inheritedAttrs, ...(op.attrs || {}) };

  // Create the new node content with inherited formatting
  const content = createNodeContent(
    op.nodeType,
    op.content,
    mergedAttrs,
    newNodeId,
    inheritedMarks,
    op.tableData
  );

  // Insert the content
  editor.chain().focus().insertContentAt(insertPos, content).run();

  return {
    success: true,
    operationIndex: index,
    nodeId: newNodeId,
  };
}

/**
 * Execute a replace operation
 */
function executeReplace(
  editor: Editor,
  op: ReplaceOperation,
  index: number
): ExecutionResult {
  const doc = editor.state.doc;
  const targetResult = findNodeById(doc, op.target.nodeId);

  if (!targetResult) {
    return {
      success: false,
      operationIndex: index,
      error: `Node not found: ${op.target.nodeId}`,
    };
  }

  const { node: targetNode, pos: targetPos } = targetResult;

  console.log(`[executeReplace] Op ${index}: node="${op.target.nodeId}" type="${targetNode.type.name}" | targetPos=${targetPos} nodeSize=${targetNode.nodeSize} content="${typeof op.content === 'string' ? op.content.substring(0, 60) : '(formatted)'}"`);

  // For table cells/headers, content is nested: tableCell -> paragraph -> text.
  // We need to replace inside the paragraph, not the cell itself.
  const isTableCell = targetNode.type.name === "tableCell" || targetNode.type.name === "tableHeader";
  let replaceNode = targetNode;
  let replacePos = targetPos;

  if (isTableCell && targetNode.childCount > 0 && targetNode.firstChild &&
      (targetNode.firstChild.type.name === "paragraph" || targetNode.firstChild.type.name === "heading")) {
    // Replace content inside the first paragraph of the cell
    replaceNode = targetNode.firstChild;
    replacePos = targetPos + 1; // Skip tableCell opening token
    console.log(`[executeReplace] Op ${index}: Table cell detected, replacing inside inner paragraph at pos=${replacePos}`);
  }

  // Extract inline marks from existing content before replacing.
  // This preserves color, font, and other styling on the replacement text.
  const inheritedMarks = extractInlineMarks(replaceNode);

  // For replace, we keep the same node type, ID, and attrs — just update text
  const from = replacePos + 1; // Start of content (after opening tag)
  const to = replacePos + replaceNode.nodeSize - 1; // End of content (before closing tag)

  // Convert content to TipTap format with inherited marks
  const tipTapContent = contentToTipTap(op.content, inheritedMarks);

  if (replaceNode.content.size === 0) {
    // Insert text into empty node
    editor.chain().focus().insertContentAt(from, tipTapContent).run();
  } else {
    // Replace existing content
    editor
      .chain()
      .focus()
      .deleteRange({ from, to })
      .insertContentAt(from, tipTapContent)
      .run();
  }

  return {
    success: true,
    operationIndex: index,
    nodeId: op.target.nodeId,
  };
}

/**
 * Execute a delete operation
 */
function executeDelete(
  editor: Editor,
  op: DeleteOperation,
  index: number
): ExecutionResult {
  const doc = editor.state.doc;
  const targetResult = findNodeById(doc, op.target.nodeId);

  if (!targetResult) {
    return {
      success: false,
      operationIndex: index,
      error: `Node not found: ${op.target.nodeId}`,
    };
  }

  const { node: targetNode, pos: targetPos } = targetResult;

  // Delete the entire node
  const from = targetPos;
  const to = targetPos + targetNode.nodeSize;

  editor.chain().focus().deleteRange({ from, to }).run();

  return {
    success: true,
    operationIndex: index,
  };
}

/**
 * Execute a move operation — relocate a node to before/after another node.
 * Preserves the full node structure including inline marks (bold, color, etc.).
 */
function executeMove(
  editor: Editor,
  op: MoveOperation,
  index: number
): ExecutionResult {
  const doc = editor.state.doc;

  // Find the source node (the one being moved)
  const sourceResult = findNodeById(doc, op.target.nodeId);
  if (!sourceResult) {
    return {
      success: false,
      operationIndex: index,
      error: `Source node not found: ${op.target.nodeId}`,
    };
  }

  // Find the destination node (the reference point)
  const destResult = findNodeById(doc, op.destination.nodeId);
  if (!destResult) {
    return {
      success: false,
      operationIndex: index,
      error: `Destination node not found: ${op.destination.nodeId}`,
    };
  }

  if (op.target.nodeId === op.destination.nodeId) {
    return {
      success: false,
      operationIndex: index,
      error: `Cannot move a node relative to itself`,
    };
  }

  const { node: sourceNode, pos: sourcePos } = sourceResult;
  const { node: destNode, pos: destPos } = destResult;

  // Capture the full JSON of the source node (preserves all marks, attrs, content)
  const sourceJson = sourceNode.toJSON();

  // Calculate insertion position based on destination
  let insertPos =
    op.position === "before" ? destPos : destPos + destNode.nodeSize;

  // If the source comes before the destination, deleting the source
  // will shift the destination's position. Adjust accordingly.
  const sourceEnd = sourcePos + sourceNode.nodeSize;
  if (sourcePos < insertPos) {
    insertPos -= sourceNode.nodeSize;
  }

  // Step 1: Delete the source node
  editor
    .chain()
    .focus()
    .deleteRange({ from: sourcePos, to: sourceEnd })
    .run();

  // Step 2: Insert the source node at the new position
  editor.chain().focus().insertContentAt(insertPos, sourceJson).run();

  return {
    success: true,
    operationIndex: index,
    nodeId: op.target.nodeId,
  };
}

/**
 * Execute a set_attrs operation — update node attributes without changing content.
 * Used for heading level changes, text alignment, indentation, etc.
 */
function executeSetAttrs(
  editor: Editor,
  op: SetAttrsOperation,
  index: number
): ExecutionResult {
  const doc = editor.state.doc;
  const targetResult = findNodeById(doc, op.target.nodeId);

  if (!targetResult) {
    return {
      success: false,
      operationIndex: index,
      error: `Node not found: ${op.target.nodeId}`,
    };
  }

  const { node: targetNode, pos: targetPos } = targetResult;

  // Merge new attrs with existing ones
  const newAttrs = { ...targetNode.attrs, ...op.attrs };

  // If level is being changed on a heading, we need to handle node type conversion
  // For example, changing heading level doesn't need type change, just attr update
  editor
    .chain()
    .focus()
    .setTextSelection(targetPos + 1)
    .updateAttributes(targetNode.type.name, newAttrs)
    .run();

  return {
    success: true,
    operationIndex: index,
    nodeId: op.target.nodeId,
  };
}

/**
 * Execute an append operation
 */
function executeAppend(
  editor: Editor,
  op: AppendOperation,
  index: number
): ExecutionResult {
  // Use pre-generated ID from the operation if provided (for cache sync),
  // otherwise generate a new one
  const newNodeId = op.newNodeId || generateNodeId();

  // Inherit formatting from the last node in the document so the
  // appended content matches the existing document's styling.
  const lastChild = editor.state.doc.lastChild;
  const inheritedMarks = lastChild ? extractInlineMarks(lastChild) : [];
  const inheritedAttrs = lastChild ? extractFormattingAttrs(lastChild) : {};

  // Merge inherited attrs with explicitly provided attrs (explicit wins)
  const mergedAttrs = { ...inheritedAttrs, ...(op.attrs || {}) };

  // Create the new node content with inherited formatting
  const content = createNodeContent(
    op.nodeType,
    op.content,
    mergedAttrs,
    newNodeId,
    inheritedMarks,
    op.tableData
  );

  // Append at the end of the document
  const docSize = editor.state.doc.content.size;

  editor.chain().focus().insertContentAt(docSize, content).run();

  return {
    success: true,
    operationIndex: index,
    nodeId: newNodeId,
  };
}

/**
 * Generate a unique comment ID
 */
function generateCommentId(): string {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 6);
  return `comment-${timestamp}-${random}`;
}

/**
 * Execute a comment operation
 */
function executeComment(
  editor: Editor,
  op: CommentOperation,
  index: number
): ExecutionResult {
  const doc = editor.state.doc;
  const targetResult = findNodeById(doc, op.target.nodeId);

  if (!targetResult) {
    return {
      success: false,
      operationIndex: index,
      error: `Node not found: ${op.target.nodeId}`,
    };
  }

  const { node: targetNode, pos: targetPos } = targetResult;
  const commentId = generateCommentId();

  // Calculate the range to apply the comment mark
  // Apply to the entire text content of the node
  const from = targetPos + 1; // Start of content
  const to = targetPos + targetNode.nodeSize - 1; // End of content

  // Only add comment mark if there's content to mark
  if (to > from) {
    editor
      .chain()
      .focus()
      .setTextSelection({ from, to })
      .setComment(commentId)
      .run();
  }

  // Create the comment data to be added to the comments state
  const commentData: CommentData = {
    id: commentId,
    content: op.commentContent,
    author: op.author || "AI",
    createdAt: new Date(),
  };

  return {
    success: true,
    operationIndex: index,
    nodeId: op.target.nodeId,
    commentData,
  };
}

/**
 * Execute an edit operation with word-level diff
 *
 * This operation computes the diff between old and new content
 * and applies granular insert/delete operations for proper track changes display.
 */
function executeEdit(
  editor: Editor,
  op: EditOperation,
  index: number
): ExecutionResult {
  const doc = editor.state.doc;
  const targetResult = findNodeById(doc, op.target.nodeId);

  if (!targetResult) {
    return {
      success: false,
      operationIndex: index,
      error: `Node not found: ${op.target.nodeId}`,
    };
  }

  const { node: targetNode, pos: targetPos } = targetResult;

  // Wrapper nodes hold their text one level deeper: the node wraps an inner
  // paragraph (tableCell/tableHeader -> paragraph -> text; listItem -> paragraph
  // -> text; blockquote -> paragraph -> text). If we compute positions against
  // the wrapper directly, contentStart (editPos + 1) lands ONE token early —
  // corrupting every edit of a list item or blockquote (e.g. replacing "sat"
  // deletes " sa" and inserts mid-word). Only tableCell was handled before.
  const WRAPPER_TYPES = new Set([
    "tableCell",
    "tableHeader",
    "listItem",
    "blockquote",
  ]);
  const isWrapper = WRAPPER_TYPES.has(targetNode.type.name);
  let editNode = targetNode;
  let editPos = targetPos;

  if (isWrapper) {
    const onlyChild =
      targetNode.childCount === 1 ? targetNode.firstChild : null;
    const isSimpleInner =
      onlyChild &&
      (onlyChild.type.name === "paragraph" || onlyChild.type.name === "heading");
    if (isSimpleInner && onlyChild) {
      // Single-paragraph wrapper: edit the inner block for correct positioning.
      editNode = onlyChild;
      editPos = targetPos + 1; // Skip the wrapper's opening token
      console.log(`[executeEdit] Op ${index}: ${targetNode.type.name} wrapper, delegating to inner ${editNode.type.name} at pos=${editPos}`);
    } else {
      // Nested / multi-block wrapper (multi-paragraph cell, list item with a
      // sublist, etc.): use the descendant position map, which resolves real
      // text positions regardless of nesting depth.
      console.log(`[executeEdit] Op ${index}: nested ${targetNode.type.name} (${targetNode.childCount} children) — using position map`);
      return executeEditWithPositionMap(editor, op, targetNode, targetPos, index);
    }
  }

  // Get the current text content of the node
  const oldText = editNode.textContent;
  const newText = op.newContent;

  console.log(`[executeEdit] Op ${index}: node="${op.target.nodeId}" type="${targetNode.type.name}" | oldTextLen=${oldText.length} newTextLen=${newText.length}`);
  console.log(`[executeEdit]   oldText="${oldText.substring(0, 120)}"`);
  console.log(`[executeEdit]   newText="${newText.substring(0, 120)}"`);

  // Compute word-level diff
  const diffResult = computeWordDiff(oldText, newText);

  // If no changes, return success without modifying
  if (!diffResult.hasChanges) {
    return {
      success: true,
      operationIndex: index,
      nodeId: op.target.nodeId,
    };
  }

  // Extract inline marks from the node before applying edits.
  // New text inserted by the diff will receive these marks so it
  // matches the paragraph's existing color, font, etc.
  const inheritedMarks = extractInlineMarks(editNode);

  // Apply the diff operations
  // We need to work from the end of the content to the beginning
  // to preserve positions as we make changes
  const contentStart = editPos + 1; // Start of content (after opening tag)

  // Build a list of edit actions from the diff
  const editActions = buildEditActions(diffResult.ops, contentStart);

  // Get inheritable marks from target so inserted text matches paragraph styling
  const _inheritableMarks = getInheritableMarks(editNode);

  // Apply edits in reverse order (from end to start) to preserve positions
  const reversedActions = [...editActions].reverse();

  for (const action of reversedActions) {
    if (action.type === "delete") {
      editor
        .chain()
        .focus()
        .deleteRange({ from: action.from!, to: action.to! })
        .run();
    } else if (action.type === "insert") {
      if (inheritedMarks.length > 0) {
        // Insert with inherited formatting marks
        editor
          .chain()
          .focus()
          .insertContentAt(action.pos!, [
            { type: "text", text: action.text!, marks: inheritedMarks },
          ])
          .run();
      } else {
        editor.chain().focus().insertContentAt(action.pos!, action.text!).run();
      }
    }
  }

  return {
    success: true,
    operationIndex: index,
    nodeId: op.target.nodeId,
  };
}

/**
 * Handle edit operations on multi-paragraph table cells.
 *
 * The agent sees concatenated textContent from all paragraphs in the cell,
 * but ProseMirror positions have gaps at paragraph boundaries. This function
 * builds a text-offset → doc-position map to correctly apply diffs across
 * paragraph boundaries.
 */
function executeEditWithPositionMap(
  editor: Editor,
  op: EditOperation,
  cellNode: any,
  cellPos: number,
  index: number
): ExecutionResult {
  const oldText = cellNode.textContent;
  const newText = op.newContent;

  const diffResult = computeWordDiff(oldText, newText);
  if (!diffResult.hasChanges) {
    return { success: true, operationIndex: index, nodeId: op.target.nodeId };
  }

  // Build map from text offset to document position
  const posMap: number[] = [];
  cellNode.descendants((child: any, childPos: number) => {
    if (child.isText && child.text) {
      const absPos = cellPos + 1 + childPos; // +1 for cell opening token
      for (let i = 0; i < child.text.length; i++) {
        posMap.push(absPos + i);
      }
    }
    return true;
  });

  // posMap[textOffset] = docPos for each character
  // Also need position just past the end for inserts at the end
  if (posMap.length > 0) {
    posMap.push(posMap[posMap.length - 1] + 1);
  }

  const inheritedMarks = extractInlineMarks(cellNode);

  // Build edit actions using the position map
  const editActions: EditAction[] = [];
  let textOffset = 0;

  for (const diffOp of diffResult.ops) {
    if (diffOp.type === "equal") {
      textOffset += diffOp.text.length;
    } else if (diffOp.type === "delete") {
      const fromPos = posMap[textOffset];
      const toPos = posMap[textOffset + diffOp.text.length - 1] + 1;
      editActions.push({ type: "delete", from: fromPos, to: toPos });
      textOffset += diffOp.text.length;
    } else if (diffOp.type === "insert") {
      const insertPos = posMap[textOffset] ?? posMap[posMap.length - 1];
      editActions.push({ type: "insert", pos: insertPos, text: diffOp.text });
    }
  }

  console.log(`[executeEdit:posMap] Op ${index}: ${editActions.length} actions for multi-paragraph cell`);

  // Apply in reverse to preserve positions
  for (const action of [...editActions].reverse()) {
    if (action.type === "delete") {
      editor.chain().focus().deleteRange({ from: action.from!, to: action.to! }).run();
    } else if (action.type === "insert") {
      if (inheritedMarks.length > 0) {
        editor.chain().focus().insertContentAt(action.pos!, [
          { type: "text", text: action.text!, marks: inheritedMarks },
        ]).run();
      } else {
        editor.chain().focus().insertContentAt(action.pos!, action.text!).run();
      }
    }
  }

  return { success: true, operationIndex: index, nodeId: op.target.nodeId };
}

/**
 * Build a list of edit actions from diff operations
 */
interface EditAction {
  type: "insert" | "delete";
  from?: number;
  to?: number;
  pos?: number;
  text?: string;
}

function buildEditActions(ops: DiffOp[], startPos: number): EditAction[] {
  const actions: EditAction[] = [];
  let currentPos = startPos;

  for (const op of ops) {
    if (op.type === "equal") {
      // No action needed, just advance position
      currentPos += op.text.length;
    } else if (op.type === "delete") {
      // Delete this text
      actions.push({
        type: "delete",
        from: currentPos,
        to: currentPos + op.text.length,
      });
      currentPos += op.text.length;
    } else if (op.type === "insert") {
      // Insert this text at current position
      actions.push({
        type: "insert",
        pos: currentPos,
        text: op.text,
      });
      // Note: don't advance currentPos for inserts since they're new text
    }
  }

  return actions;
}

// ===========================================
// FORMATTING INHERITANCE HELPERS
// ===========================================

/**
 * Mark type names that represent metadata, not visual formatting.
 * These should NOT be inherited when creating new content.
 */
const METADATA_MARK_TYPES = new Set(["comment", "insertion", "deletion"]);

/**
 * Combined set of marks that should NOT be inherited.
 * Includes both metadata marks and stylistic marks.
 */
const NON_INHERITABLE_MARKS = new Set([
  ...Array.from(METADATA_MARK_TYPES),
  ...Array.from(STYLISTIC_MARK_TYPES),
]);

/**
 * Node attributes that represent visual formatting and should be
 * inherited from surrounding nodes when inserting new content.
 */
const INHERITED_FORMATTING_ATTRS = new Set([
  "textAlign",
  "indent",
  "textIndent",
]);

/**
 * Extract inline formatting marks from a node's text content.
 *
 * Walks the node's descendants and returns ProseMirror mark JSON
 * from the first text node that has marks, filtering out metadata
 * marks (comments, track changes) AND stylistic marks (bold, italic, etc.).
 *
 * Only inherits document-wide formatting like color, font family, and font size.
 * This prevents inserted text from unexpectedly becoming bold or superscript
 * just because it's inserted near styled text.
 */
function extractInlineMarks(node: any): any[] {
  const marks: any[] = [];
  let found = false;

  node.descendants((child: any) => {
    if (found) return false;
    if (child.isText && child.marks && child.marks.length > 0) {
      for (const mark of child.marks) {
        if (!NON_INHERITABLE_MARKS.has(mark.type.name)) {
          marks.push(mark.toJSON());
        }
      }
      found = true;
      return false;
    }
    return true;
  });

  return marks;
}

/**
 * Extract formatting-relevant attributes from a node.
 *
 * Only includes attributes that affect visual presentation
 * (textAlign, indent) — NOT structural metadata like
 * patentSequence or claimMetadata.
 */
function extractFormattingAttrs(node: any): Record<string, any> {
  const attrs: Record<string, any> = {};

  for (const [key, value] of Object.entries(node.attrs || {})) {
    if (
      INHERITED_FORMATTING_ATTRS.has(key) &&
      value !== null &&
      value !== undefined
    ) {
      // Skip default values
      if (key === "textAlign" && value === "left") continue;
      attrs[key] = value;
    }
  }

  return attrs;
}

/**
 * Convert mark type to TipTap mark format
 */
function markTypeToTipTap(mark: MarkType): { type: string } {
  return { type: mark };
}

/**
 * Convert content (string or formatted segments) to TipTap content format.
 *
 * When `inheritedMarks` is provided (ProseMirror mark JSON from a reference
 * node), plain text content will receive those marks — preserving the
 * surrounding document's color, font, and other inline formatting.
 *
 * Explicitly provided marks on FormattedTextSegments take precedence
 * over inherited marks.
 */
function contentToTipTap(content: ContentType, inheritedMarks?: any[]): any[] {
  if (!content) return [];

  // If it's a plain string, return text node with inherited marks
  if (typeof content === "string") {
    if (!content) return [];
    const textNode: any = { type: "text", text: content };
    if (inheritedMarks && inheritedMarks.length > 0) {
      textNode.marks = inheritedMarks;
    }
    return [textNode];
  }

  // If it's formatted segments, convert each segment
  // Explicit marks take precedence over inherited marks
  return content.map((segment: FormattedTextSegment) => {
    const textNode: any = {
      type: "text",
      text: segment.text,
    };

    if (segment.marks && segment.marks.length > 0) {
      // Explicit marks provided — use them
      textNode.marks = segment.marks.map(markTypeToTipTap);
    } else if (inheritedMarks && inheritedMarks.length > 0) {
      // No explicit marks — use inherited ones
      textNode.marks = inheritedMarks;
    }

    return textNode;
  });
}

/**
 * Merge two mark arrays. Explicit marks take precedence over inherited
 * marks when they share the same type (e.g., both have "textStyle").
 */
function mergeMarks(inherited: any[], explicit: any[]): any[] {
  if (explicit.length === 0) return inherited;
  if (inherited.length === 0) return explicit;

  const result = [...inherited];
  for (const expMark of explicit) {
    const existingIdx = result.findIndex((m: any) => m.type === expMark.type);
    if (existingIdx >= 0) {
      // Merge attrs (explicit wins for overlapping keys)
      result[existingIdx] = {
        ...result[existingIdx],
        attrs: {
          ...(result[existingIdx].attrs || {}),
          ...(expMark.attrs || {}),
        },
      };
    } else {
      result.push(expMark);
    }
  }
  return result;
}

/**
 * Create node content in TipTap format.
 *
 * When `inheritedMarks` is provided, text content will receive those
 * marks — preserving the surrounding document's inline formatting
 * (color, font family, font size, etc.).
 *
 * If `attrs` contains text-level style properties (fontFamily, fontSize),
 * they are extracted and applied as textStyle marks on the text content,
 * since TipTap uses marks (not node attributes) for inline text styling.
 */
function createNodeContent(
  nodeType: NodeType,
  content: ContentType,
  attrs?: NodeAttrs,
  nodeId?: string,
  inheritedMarks?: any[],
  tableData?: string[][]
): any {
  const baseAttrs: Record<string, any> = {
    ...(attrs || {}),
  };

  if (nodeId) {
    baseAttrs.id = nodeId;
  }

  // Extract text-level style properties from attrs and convert to textStyle marks.
  // In TipTap, fontFamily and fontSize are text marks (via TextStyle extension),
  // not node-level attributes.
  const textStyleAttrs: Record<string, any> = {};
  if (baseAttrs.fontFamily) {
    textStyleAttrs.fontFamily = baseAttrs.fontFamily;
    delete baseAttrs.fontFamily;
  }
  if (baseAttrs.fontSize) {
    textStyleAttrs.fontSize = baseAttrs.fontSize;
    delete baseAttrs.fontSize;
  }

  let finalMarks = inheritedMarks || [];
  if (Object.keys(textStyleAttrs).length > 0) {
    finalMarks = mergeMarks(finalMarks, [
      { type: "textStyle", attrs: textStyleAttrs },
    ]);
  }

  const tipTapContent = contentToTipTap(
    content,
    finalMarks.length > 0 ? finalMarks : undefined
  );

  switch (nodeType) {
    case "paragraph":
      return {
        type: "paragraph",
        attrs: baseAttrs,
        content: tipTapContent,
      };

    case "heading":
      return {
        type: "heading",
        attrs: {
          ...baseAttrs,
          level: attrs?.level || 1,
        },
        content: tipTapContent,
      };

    case "bulletList":
      return {
        type: "bulletList",
        attrs: baseAttrs,
        content: [
          {
            type: "listItem",
            content: [
              {
                type: "paragraph",
                content: tipTapContent,
              },
            ],
          },
        ],
      };

    case "orderedList":
      return {
        type: "orderedList",
        attrs: baseAttrs,
        content: [
          {
            type: "listItem",
            content: [
              {
                type: "paragraph",
                content: tipTapContent,
              },
            ],
          },
        ],
      };

    case "listItem":
      return {
        type: "listItem",
        attrs: baseAttrs,
        content: [
          {
            type: "paragraph",
            content: tipTapContent,
          },
        ],
      };

    case "blockquote":
      return {
        type: "blockquote",
        attrs: baseAttrs,
        content: [
          {
            type: "paragraph",
            content: tipTapContent,
          },
        ],
      };

    case "codeBlock":
      // Code blocks typically don't have marks, use plain text
      const plainText =
        typeof content === "string"
          ? content
          : (content as FormattedTextSegment[]).map(s => s.text).join("");
      return {
        type: "codeBlock",
        attrs: baseAttrs,
        content: plainText ? [{ type: "text", text: plainText }] : [],
      };

    case "pageBreak":
      return {
        type: "pageBreak",
        attrs: baseAttrs,
      };

    case "horizontalRule":
      return {
        type: "horizontalRule",
      };

    case "table": {
      // Build an HTML table string for insertion. TipTap's insertContentAt
      // accepts HTML and its table parsing (via parseHTML rules) is more
      // reliable than raw JSON for complex nested structures.
      const escapeHtml = (text: string) =>
        text
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;");

      if (!tableData || tableData.length === 0) {
        // Fallback: empty 2x2 table
        return "<table><tr><th><p></p></th><th><p></p></th></tr><tr><td><p></p></td><td><p></p></td></tr></table>";
      }
      const htmlRows = tableData
        .map((row, rowIndex) => {
          const tag = rowIndex === 0 ? "th" : "td";
          const cells = row
            .map(
              (cellText) =>
                `<${tag}><p>${cellText ? escapeHtml(cellText) : ""}</p></${tag}>`
            )
            .join("");
          return `<tr>${cells}</tr>`;
        })
        .join("");
      return `<table>${htmlRows}</table>`;
    }

    default:
      // Default to paragraph for unknown types
      return {
        type: "paragraph",
        attrs: baseAttrs,
        content: tipTapContent,
      };
  }
}

/**
 * Validate a patch before execution
 *
 * @param editor - The TipTap editor instance
 * @param patch - The patch to validate
 * @returns Validation result
 */
export function validatePatch(
  editor: Editor,
  patch: OperationPatch
): { valid: boolean; errors: string[] } {
  const errors: string[] = [];
  const doc = editor.state.doc;

  for (let i = 0; i < patch.operations.length; i++) {
    const op = patch.operations[i];

    // Check if target exists (for operations that need it)
    if ("target" in op && "nodeId" in op.target) {
      const targetResult = findNodeById(doc, op.target.nodeId);
      if (!targetResult) {
        errors.push(
          `Operation ${i}: Target node not found: ${op.target.nodeId}`
        );
      }
    }

    // Validate content is provided where needed
    if (
      isInsertOperation(op) ||
      isReplaceOperation(op) ||
      isAppendOperation(op)
    ) {
      // Table operations use tableData instead of content
      const isTableOp =
        (isInsertOperation(op) || isAppendOperation(op)) &&
        op.nodeType === "table" &&
        op.tableData;
      if (!isTableOp && (op.content === undefined || op.content === null)) {
        errors.push(
          `Operation ${i}: Content is required for ${op.op} operation`
        );
      }
    }
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

// ===========================================
// TRACK CHANGE OPERATIONS
// ===========================================

function executeAcceptChange(
  editor: Editor,
  op: AcceptChangeOperation,
  index: number
): ExecutionResult {
  try {
    const commands = editor.commands as any;
    if (typeof commands.acceptChange === "function") {
      commands.acceptChange(op.changeId);
      return { success: true, operationIndex: index };
    }
    return {
      success: false,
      operationIndex: index,
      error: "Track changes extension not available",
    };
  } catch (e: any) {
    return {
      success: false,
      operationIndex: index,
      error: `Failed to accept change ${op.changeId}: ${e.message}`,
    };
  }
}

function executeRejectChange(
  editor: Editor,
  op: RejectChangeOperation,
  index: number
): ExecutionResult {
  try {
    const commands = editor.commands as any;
    if (typeof commands.rejectChange === "function") {
      commands.rejectChange(op.changeId);
      return { success: true, operationIndex: index };
    }
    return {
      success: false,
      operationIndex: index,
      error: "Track changes extension not available",
    };
  } catch (e: any) {
    return {
      success: false,
      operationIndex: index,
      error: `Failed to reject change ${op.changeId}: ${e.message}`,
    };
  }
}

function executeAcceptAllChanges(
  editor: Editor,
  _op: AcceptAllChangesOperation,
  index: number
): ExecutionResult {
  try {
    const commands = editor.commands as any;
    if (typeof commands.acceptAllChanges === "function") {
      commands.acceptAllChanges();
      return { success: true, operationIndex: index };
    }
    return {
      success: false,
      operationIndex: index,
      error: "Track changes extension not available",
    };
  } catch (e: any) {
    return {
      success: false,
      operationIndex: index,
      error: `Failed to accept all changes: ${e.message}`,
    };
  }
}

function executeRejectAllChanges(
  editor: Editor,
  _op: RejectAllChangesOperation,
  index: number
): ExecutionResult {
  try {
    const commands = editor.commands as any;
    if (typeof commands.rejectAllChanges === "function") {
      commands.rejectAllChanges();
      return { success: true, operationIndex: index };
    }
    return {
      success: false,
      operationIndex: index,
      error: "Track changes extension not available",
    };
  } catch (e: any) {
    return {
      success: false,
      operationIndex: index,
      error: `Failed to reject all changes: ${e.message}`,
    };
  }
}

// ===========================================
// COMMENT MANAGEMENT OPERATIONS
// ===========================================

function executeRemoveComment(
  editor: Editor,
  op: RemoveCommentOperation,
  index: number
): ExecutionResult {
  try {
    const commands = editor.commands as any;
    if (typeof commands.unsetComment === "function") {
      commands.unsetComment(op.commentId);
      return { success: true, operationIndex: index };
    }
    return {
      success: false,
      operationIndex: index,
      error: "Comment extension not available",
    };
  } catch (e: any) {
    return {
      success: false,
      operationIndex: index,
      error: `Failed to remove comment ${op.commentId}: ${e.message}`,
    };
  }
}

function executeReplyComment(
  editor: Editor,
  op: ReplyCommentOperation,
  index: number
): ExecutionResult {
  try {
    // Reply comments are handled at the UI/data layer, not as editor marks.
    // The comment data structure supports replies. We emit a comment data
    // object so the UI callback can add the reply.
    return {
      success: true,
      operationIndex: index,
      commentData: {
        id: op.commentId,
        content: op.replyContent,
        author: op.author || "AI Assistant",
        createdAt: new Date(),
      },
    };
  } catch (e: any) {
    return {
      success: false,
      operationIndex: index,
      error: `Failed to reply to comment ${op.commentId}: ${e.message}`,
    };
  }
}
